/**
 * Chat widget view for the chatting pane/screen.
 */
import Tooltip from '../../../node_modules/bootstrap/js/src/tooltip.js';
import { flowActionTypes } from '../../chatbot/flowConstants.js';
import springyView from '../../tools/SpringyView.js';
import { focusFirst } from '../../tools/focusUtil.js';
import { createAltchaWidget, resetAltchaWidget } from '../../tools/altchaCaptcha.js';

const chatView = Object.create(springyView);

/**
 * @prop {patronSession} patronSession Data about patron.
 */
chatView.patronSession = null;
/**
 * @prop {Array} operatorsList Array of current ops in chat.
 */
chatView.operatorsList = [];
/**
 * @prop {Array} currentTypers Array of current ops typing.
 */
chatView.currentTypers = [];
/**
 * @prop {Array} filesToUpload Array of img files to send
 */
chatView.filesToUpload = [];
/**
 * @prop {Object} events Default events on this view.
 */
chatView.events = {
    'click .s-lch-btn-sound': 'toggleSoundButton',
    'keyup #s-lch-post-input': 'patronTyping',
    'keydown #s-lch-post-input': 'triggerSubmitOnEnter',
    'submit #s-lch-post-form': 'messageSubmit',
    'click .s-lch-btn-trans': 'sendTranscript',
    'click .s-lch-header-end .s-lch-btn-close': 'closeWidget',
    'click #s-lch-rate-form .s-lch-btn-close': 'reloadCloseChat',
    'click .s-lch-msg-op a': 'followLink',
    'click a.s-la-cfile[data-preview]': 'showFileUploadPreview',
    'click a.btn-item-renew': '_handleBotRenew',
    'click a': 'openNewTab',
    'click button[data-responseid]': 'handleBotResponseOpts',
    'keydown': 'trapFocus',
    'paste #s-lch-post-input': 'handlePaste',
    'click .s-lch-img-wrapper>.remove-btn': 'removeImgPreview',
};
/**
 * @prop {Object} listeners Model events to trigger methods.
 */
chatView.listeners = {
    'opJoin': 'showOpJoin',
    'opLeave': 'showOpLeave',
    'opAway': 'showOpAway',
    'endChat': 'endChatDisplay',
    'chatSubscribe': 'chatSubscribeDisplay',
    'chatReconnecting': 'showReconnecting',
    'chatReconnected': 'showReconnected',
    'chatError': 'showChatError',
    'chatBlockedError': 'showBlockedError',
    'requestContact': 'showContactRequestForm',
    'chatAway': 'showChatAwayStatus',
    'patronMessagePosted': 'showPatronMessage',
    'change:chatting': 'enableChatting',
    'change:peelingParent': 'peeledOutDisplay',
    'change:connected': 'setConnectState',
    'showGreeting': 'showGreeting',
    'deptGreeting': 'showDeptGreeting',
    'chatMsgNotSendable': 'showMessageNotSendable',
    'bot:start': 'activateBot',
    'bot:startsession': 'startBot',
    'bot:stop': 'stopBot',
    'bot:action': 'showBotMessage',
    'bot:skipbot': 'skipBot',
    'bot:end': 'endBot',
    'bot:action:page': 'showPagedOptions',
    'captcha:required': 'showCaptchaPrompt',
    'captcha:done': 'removeCaptchaPrompt',
};
/**
 * @prop {Function} missedChatTime Timer function.
 */
chatView.missedChatTime = null;
/**
 * @prop {Function} Time fallback timer function.
 */
chatView.timedFallbackTime = null;
/**
 * @prop {Boolean} isBotActive Is the chatbot currently chatting.
 */
chatView.isBotActive = false;
/**
 * @prop {HTMLElement|null} captchaWidget altcha-widget element when captcha is shown in the chat pane.
 */
chatView.captchaWidget = null;
/**
 * Wrapper for springyView.init in case we need extra functionality.
 * @param {Object} config Config data.
 * @param {chatWidget} config.model Widget object for this view.
 * @param {patronSession} patronSession Patron session object.
 * @param {eventEmitter} emitter Sui.eventEmitter object.
 */
chatView.initialize = function (config) {
    if (config.patronSession) {
        this.patronSession = config.patronSession;
    }
    if (config.emitter) {
        this.emitter = config.emitter;
    }
    if (config.operatorsList) {
        this.operatorsList = config.operatorsList;
    }

    config.$el = window.$('#chat');
    this.init(config);
};
/**
 * Handler: Keyup on chat input field.
 * @param {Object} e Keyup event.
 */
chatView.patronTyping = function (e) {
    // no typing actions for bot
    if (this.isBotActive) {
        return;
    }
    if (e.key && e.key === 'Enter') {
        return;
    }
    // trigger a typing event.
    const $input = window.$(e.target);
    const message = $input.val().trim();
    const isTyping = (message !== '');
    this.emitter.trigger('isTyping', isTyping);
};
/**
 * Trigger form submission on enter in textarea
 * @param {Object} e Keydown event.
 */
chatView.triggerSubmitOnEnter = function (e) {
    if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.$form.trigger('submit');
    }
};
/**
 * Handle submissions from form to bot.
 */
chatView.handleBotResponseSubmit = function () {
    const formData = new FormData(this.$form[0]);

    const actionId = Number(formData.get('action_id') || 0);
    const responseId = Number(formData.get('responseid') || 0);
    let text = formData.get('msg') || '';
    text = text.trim();

    // A button or a select option
    const selectedResponse = this.$form[0].querySelector(`button[data-responseid="${responseId}"], option[value="${responseId}"]`);
    const isOther = Number(selectedResponse ? (selectedResponse.dataset.other || 0) : 0) === 1;
    const page = Number(selectedResponse ? (selectedResponse.dataset.page || 0) : 0);
    const back = selectedResponse ? !!selectedResponse.dataset.back : false;
    if (!selectedResponse || isOther) {
        if (text === '') {
            return;
        }
        this.showPatronMessage(text);
        this.emitter.trigger('bot:freetext', { actionId, text });
        return;
    }
    // paginate response actions (some actions)
    if (page > 0) {
        this.emitter.trigger('bot:response:page', { actionId, page });
        return;
    }
    // go back a stage (some actions)
    if (back) {
        this.emitter.trigger('bot:response:back', { actionId });
        return;
    }

    // Use response label text
    text = selectedResponse.innerText;
    this.showPatronMessage(text);
    this.emitter.trigger('bot:response', { actionId, responseId });
};

/**
 * Remove a pasted image
 * @param {Event} ev event
 */
chatView.removeImgPreview = function (ev) {
    // Remove from the data array and clean up
    const target = ev.target;
    const wrapper = target.closest('.s-lch-img-wrapper');
    if (wrapper) {
        this.filesToUpload = this.filesToUpload.filter((item) => item.id !== wrapper.dataset.id);
        const url = wrapper.querySelector('img').src;
        URL.revokeObjectURL(url);
        wrapper.remove();
    } else {
        this.imgContainer.innerHTML = '';
        this.filesToUpload = [];
    }
};

/**
 * Handle pasting into the editor so we can handle images pasted.
 * @param {Event} ev Paste event.
 */
chatView.handlePaste = function (ev) {
    if (!this.imgContainer) {
        return;
    }

    ev = ev.originalEvent;
    const clipboardData = ev.clipboardData;

    const items = clipboardData.items;
    try {
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                ev.preventDefault();

                // Remove errors
                this.imgContainer.querySelectorAll('.text-danger').forEach((elem) => elem.remove());
                if (this.filesToUpload.length > 0) {
                    this.imgContainer.insertAdjacentHTML('beforeend', `<div class="text-danger" style="align-self:anchor-center;">${this.model.get('translation').ch_pasted_img_count}</div>`);
                    return;
                }

                const file = items[i].getAsFile();
                if (file.size >= 20971520) {
                    this.imgContainer.insertAdjacentHTML('beforeend', `<span class="text-danger">${this.model.get('translation').ch_pasted_img_toolarge}</span>`);
                    return;
                }
                const fileId = window.crypto.randomUUID();
                this.filesToUpload.push({ id: fileId, file: file });
                const blobUrl = URL.createObjectURL(file);
                // Add the image preview
                const wrapper = this.templateEng.render('clipboard_image_wrapper.njk', { id: fileId, url: blobUrl, removeLabel: this.model.get('translation').ch_remove });
                this.imgContainer.insertAdjacentHTML('beforeend', wrapper);

                return;
            }
        }
    } catch (error) {
        console.error(error);
    }
};
/**
 * Handle form submission
 * @param {Object} e Submit event.
 */
chatView.messageSubmit = function (e) {
    e.preventDefault();

    if (this.isBotActive) {
        // Handle submissions from form to bot.
        this.handleBotResponseSubmit();
        return;
    }
    const msgTxt = this.$msgInput.val().trim();
    this.emitter.trigger('messageSubmit', msgTxt);
};
/**
 * Clear the textarea and pasted images
 * @param {Boolean} focus Focus on field too?
 */
chatView.clearTextarea = function (focus) {
    if (this.imgContainer) {
        const images = this.imgContainer.querySelectorAll('img');
        images.forEach((img) => URL.revokeObjectURL(img.src));
        this.imgContainer.innerHTML = '';
        this.filesToUpload = [];
    }

    this.$msgInput.val('');
    if (focus) {
        this.$msgInput.focus();
    }
};
/**
 * Intercept message link clicks in op messages in case we need to do something special
 * @param {Event} ev Click event.
 */
chatView.followLink = function (ev) {
    const link = ev.currentTarget;
    const url = (link.href) ? link.href : '';
    if (url === '') {
        ev.preventDefault();
    }
};
/**
 * Add a chat message to the widget main area
 * @param {Object} data Data for template.
 * @param {String} data.msg the message
 * @param {String} [data.ts] timestamp for the message
 * @param {String} data.name name of the message author
 * @param {String} data.opId Op_id for ops
 * @param {String} data.role op or user
 * @param {Boolean} data.show_meta Are we outputting metadata or not
 */
chatView.showChatMsg = function (data) {
    if (!data.msg) {
        return;
    }

    let html = '';
    data.id = (data.id) ? data.id : '';
    data.opId = (data.opId) ? data.opId : 0;
    data.ts = (!data.ts) ? new Date() : new Date(data.ts);
    data.ts_html = '';
    data.ts_time = '';

    // we only show metadata on request or in some situations
    if (typeof data.show_meta === 'undefined') {
        // should we show the timestamp, profile, name, etc.
        data.show_meta = this.checkShowMeta(data.role, data.ts, data.opId);
    }
    // format time for time element pubdate attr and for human readability
    if (data.ts !== '' && data.ts !== 'Invalid Date') {
        data.ts_html = this.htmlTime(data.ts);
        data.ts_time = data.ts.toLocaleTimeString();
    }

    if (data.role === 'user') {
        data.name = this.model.get('translation').ch_user_pronoun;
        html = this.templateEng.render('message_patron.njk', data);
    } else if (data.role === 'op') {
        data.profile_image = (!data.profile_image) ? { path: '', color: '' } : data.profile_image;
        html = this.templateEng.render('message_op.njk', data);
    }

    this.log.insertAdjacentHTML('beforeend', html);
};
/**
 * Check to see if we should show a timestamp, profile, or name.
 * Based on various factors like role, last message type, and last message time.
 * @param {String} role User or Op or Sys.
 * @param {Date} ts Date obj for when msg was posted.
 * @param {Number} opId Op_id for ops.
 * @return {Boolean}
 */
chatView.checkShowMeta = function (role, ts, opId) {
    if (typeof ts === 'undefined' || ts === 'Invalid Date') {
        ts = new Date();
    }
    let m_class;
    switch (role) {
        case 'op':
            m_class = `s-lch-msg-op-${opId}`;
            break;
        case 'user':
            m_class = 's-lch-msg-patron';
            break;
        case 'sys':
            m_class = 's-lch-msg-sys';
            break;
        default:
            return true;
    }

    const last = [...this.log.querySelectorAll('.s-lch-msg')].at(-1);
    if (!last || !last.classList.contains(m_class)) {
        return true;
    }
    const time = last.querySelector('time');
    if (time) {
        const d2 = new Date(time.getAttribute('pubdate'));
        const diff = this.checkMinuteDiff(ts, d2, 5);
        if (diff) {
            return true;
        }
    }
    return false;
};
/**
 * Compare to Date objects to see if they are more than minutes apart
 * @param {Date} d1 Date object
 * @param {Date} d2 Date object
 * @param {Integer} minutes Minutes of difference to check
 * @return {Boolean} True if more than minutes difference
 */
chatView.checkMinuteDiff = function (d1, d2, minutes) {
    // get difference in ms
    let diff = Math.abs(d1 - d2);
    // convert to minutes
    diff = Math.round((diff / 1000) / 60);
    return diff >= minutes;
};
/**
 * For system status type messages (wont go in the transcript, wont get timestamps)
 * @param {Object} data Data for template.
 * @param {String} data.msg text of the message
 * @param {String} data.id an html id to keep certain messages unique (like the reconnecting message)
 */
chatView.showChatSysMsg = function (data) {
    if (!data.msg) {
        return;
    }
    data.id = (!data.id) ? '' : data.id;

    data.ts = (!data.ts) ? new Date() : new Date(data.ts);
    if (data.ts !== '' && data.ts !== 'Invalid Date') {
        data.ts_html = this.htmlTime(data.ts);
        data.ts_time = data.ts.toLocaleTimeString();
    }
    data.show_meta = this.checkShowMeta('sys', data.ts);
    if (data.id !== '') {
        const msg = this.log.querySelector(`#${data.id}`);
        if (msg) {
            msg.remove();
        }
    }
    this.log.insertAdjacentHTML('beforeend', this.templateEng.render('message_system.njk', data));

    this.scrollLog();
};
/**
 * Format a datetime string into the appropriate format for html time element
 * @param {String} date a js date string
 * @return {String} a datetime string in RFC 3339 format.
 */
chatView.htmlTime = function (date) {
    date = new Date(date);
    let o = '';
    // year
    o += `${date.getFullYear()}-`;
    // month
    o += `${date.getMonth() < 10 ? '0' : ''}${date.getMonth() + 1}-`;
    // day
    o += `${date.getDate() < 10 ? '0' : ''}${date.getDate()}`;
    // hour
    o += `T${date.getHours() < 10 ? '0' : ''}${date.getHours()}:`;
    // minutes
    o += `${date.getMinutes() < 10 ? '0' : ''}${date.getMinutes()}:`;
    // seconds
    o += `${date.getSeconds() < 10 ? '0' : ''}${date.getSeconds()}`;
    // timezone offset
    o += (-date.getTimezoneOffset() < 0 ? '-' : '+');
    o += `${Math.abs(date.getTimezoneOffset() / 60) < 10 ? '0' : ''}${Math.abs(date.getTimezoneOffset() / 60)}:00`;
    return o;
};
/**
 * Scroll log to bottom
 */
chatView.scrollLog = function () {
    try {
        this.log.parentElement.scrollTop = this.log.scrollHeight;
    } catch {
        // ignore
    }
};
/**
 * Show file upload message.
 * @param {String} fileMessage File message text
 */
chatView.showFileUpload = function (fileMessage) {
    this.showChatMsg({ msg: fileMessage, name: 'You', role: 'user' });
    this.scrollLog();
    this.$msgInput.focus();
};
/**
 * Handler: Contact form submit.
 * @param {Object} e Submit event
 */
chatView.contactSave = function (e) {
    e.preventDefault();
    this.contactForm.querySelector('.s-lch-form-error')?.remove();
    const contactField = this.contactForm.querySelector('input[name=client_contact]');
    const contact = contactField
        ? contactField.value.trim()
        : '';
    if (!this.model.validateContact(contact)) {
        this.showValidationError(contactField, this.model.get('valid_form_c'));
        return;
    }
    // Reformat and set
    this.patronSession.setContactInfo(contact);

    const nameField = this.contactForm.querySelector('input[name=client_name]');
    if (nameField) {
        const client_name = nameField.value.trim();
        if (this.model.get('enable_anon') === false && client_name === '') {
            this.showValidationError(nameField, this.model.get('valid_form_n'));
            return;
        }
        // don't overwrite the anonymous generated name with blank
        if (client_name !== '') {
            this.patronSession.set('name', client_name);
        }
    }

    let isValid = true;
    /**
     * NOTE: code repeated in chatWidgetLoginView.validateCustomFields we should probably centralize it somewhere else.
     */
    const customFields = this.model.get('customFields');
    if (Array.isArray(customFields) && customFields.length > 0) {
        customFields.forEach((field) => {
            if (!field.show) {
                return;
            }
            const fieldInput = this.contactForm.querySelector(`#s-lch-user${field.id}`);
            if (!fieldInput) {
                return;
            }
            const value = fieldInput.value.trim();
            if (field.required) {
                let invalid = false;
                if (field.type === 'l'
                    && (!fieldInput.selectedIndex || fieldInput.selectedIndex <= 0)) {
                    // is it unselected or the first empty/0 option.
                    invalid = true;
                } else if (value === '') {
                    invalid = true;
                }
                if (invalid) {
                    isValid = false;
                    this.showValidationError(fieldInput, this.model.get('translation').required || 'Required');
                    fieldInput.focus();
                    return;
                }
            }
            this.patronSession.set(`user${field.id}`, {
                name: field.name,
                val: value,
            });
        });
    }

    if (!isValid) {
        return;
    }

    this.contactForm.querySelectorAll('button[type=submit], input').forEach((el) => {
        el.disabled = true;
    });

    // we haven't started a chat yet
    if (this.model.get('cascade') === null) {
        const form = this.contactForm.querySelector('form');
        if (form) {
            form.innerHTML = `<p>${this.model.get('translation').ch_thanks}</p>`;
        }
        this.$msgInput.focus();
        this.emitter.trigger('startSession');
        return;
    }
    this.emitter.trigger('contactSave', this.contactForm.querySelector('form'));
};
/**
 * Mark contact form as successfully saved.
 */
chatView.contactFormSuccess = function () {
    const form = this.contactForm.querySelector('form');
    if (form) {
        form.innerHTML = `<p>${this.model.get('translation').ch_contact_info_saved}</p>`;
    }
    this.$msgInput.focus();
    if (this.$msgInput.val() !== '') {
        this.$form.submit();
    }
};
/**
 * Callback: Contact save error.
 */
chatView.contactSaveError = function () {
    this.contactForm.insertAdjacentHTML('beforeend', `<p>${this.model.get('translation').ch_contact_info_not_saved}</p>`);
    this.contactForm.querySelectorAll('button[type=submit], input').forEach((el) => {
        el.disabled = false;
    });
};
/**
 * Show the contact request form in the chatting area
 * @param {Boolean} allFields Show more than just contact
 */
chatView.showContactRequestForm = function (allFields) {
    if (this.patronSession.get('client_contact') !== '') {
        return;
    }
    if (typeof allFields === 'undefined') {
        allFields = false;
    }
    // remove if there is an existing form from earlier
    if (this.contactForm !== null) {
        this.contactForm.remove();
        this.contactForm = null;
    }
    // add the new one
    const templateData = this.model.toJSON();
    templateData.allFields = allFields;
    templateData.channel = this.patronSession.getChannel();

    this.log.insertAdjacentHTML('beforeend', this.templateEng.render('form_contactrequest.njk', templateData));
    this.contactForm = this.el.querySelector('#s-lch-msg-contact');
    // attach handler
    this.contactForm.querySelector('form')?.addEventListener('submit', this.contactSave.bind(this));
    this.contactForm.querySelector('input[name=client_contact]')?.focus();

    this.scrollLog();
};
/**
 * Show the captcha in the chatting area before a session is started.
 * If already shown it is reset (e.g. server rejected it).
 * @param {String} error Optional error text.
 */
chatView.showCaptchaPrompt = function (error = '') {
    let prompt = this.el.querySelector('#s-lch-msg-captcha');
    if (prompt === null) {
        this.log.insertAdjacentHTML('beforeend', this.templateEng.render('captcha_prompt.njk', this.model.toJSON()));
        prompt = this.el.querySelector('#s-lch-msg-captcha');
        this.captchaWidget = createAltchaWidget(prompt.querySelector('.s-lch-captcha'), {
            host: window.location.hostname,
            onVerified: (payload) => {
                prompt.querySelector('.s-lch-captcha-error').classList.add('hidden');
                this.emitter.trigger('captcha:verified', payload);
            },
            onExpired: () => {
                this.emitter.trigger('captcha:verified', '');
            },
        });
    } else {
        resetAltchaWidget(this.captchaWidget);
    }
    const errorEl = prompt.querySelector('.s-lch-captcha-error');
    errorEl.textContent = error;
    errorEl.classList.toggle('hidden', error === '');
    this.scrollLog();
};
/**
 * Remove the captcha from the chatting area once a session is started.
 */
chatView.removeCaptchaPrompt = function () {
    this.el.querySelector('#s-lch-msg-captcha')?.remove();
    this.captchaWidget = null;
};
/**
 * Show the comments/rating form.
 * @param {Boolean} isBot Is it the bot rating.
 */
chatView.showRatingForm = function (isBot = false, { survey_uuid = '', rate_1 = '', rate_4 = '' } = {}) {
    const templateData = this.model.toJSON();
    templateData.channel = this.patronSession.getChannel();
    templateData.isBot = isBot;
    // bot nps overwrites what the widget has by default
    if (isBot && survey_uuid !== '') {
        templateData.survey_uuid = survey_uuid;
        templateData.rate_1 = rate_1;
        templateData.rate_4 = rate_4;
    }
    if (!this.patronSession.isContactSMS()) {
        templateData.clientContact = this.patronSession.get('client_contact');
    }
    const html = this.templateEng.render('form_rating.njk', templateData);
    this.log.insertAdjacentHTML('beforeend', html);
    const form = this.log.querySelector('#s-lch-rate-form');
    if (!form) {
        return;
    }
    form.addEventListener('submit', this.saveRating.bind(this));

    form.querySelectorAll('input[type="checkbox"]').forEach((el) => {
        el.addEventListener('change', this.toggleCommentContactInput.bind(this));
    });

    this.scrollLog();
    this.hideForm();
    this.disableForm();

    const focusable = form.querySelector('input[type="radio"]');
    if (focusable) {
        focusable.focus();
    }
};
/**
 * Show the contact input, if necessary, when requesting followup/transcript on comment form.
 */
chatView.toggleCommentContactInput = function () {
    const ratingForm = this.log.querySelector('#s-lch-rate-form');
    const clientContact = ratingForm.querySelector('#rate_client_contact');
    const contactContain = clientContact ? clientContact.closest('div.form-group') : null;
    const anyChecked = ratingForm.querySelectorAll('input[type="checkbox"]:checked');
    if (anyChecked.length === 0) {
        if (contactContain) {
            contactContain.classList.add('hidden');
            clientContact.removeAttribute('required');
        }
    } else if (contactContain) {
        contactContain.classList.remove('hidden');
        clientContact.setAttribute('required', true);
    }
};
/**
 * Handler: rating form submit
 * @param {Object} e Event object.
 */
chatView.saveRating = function (e) {
    e.preventDefault();
    const form = e.target;
    form.querySelector('.s-lch-form-error')?.remove();
    const formdata = new FormData(form);

    const commentField = form.querySelector('textarea[name=client_comment]');
    const followup = formdata.get('request_followup');
    const transcript = formdata.get('email_transcript');
    const contactField = form.querySelector('input[name=client_contact]');
    if (followup || transcript) {
        const contact = formdata.get('client_contact').toString();
        if (contact === '' || !this.model.validateContact(contact)) {
            this.showValidationError(contactField, this.model.get('valid_form_c'));
            return;
        }
        // Reformat and set
        this.patronSession.setContactInfo(contact);
    }

    if (transcript && this.patronSession.isContactSMS()) {
        this.showValidationError(contactField, this.model.get('valid_form_c'));
        return;
    }
    if (followup && commentField.value === '') {
        this.showValidationError(commentField, this.model.get('translation').ch_include_comment);
        return;
    }
    form.querySelectorAll('button[type=submit], fieldset, textarea, input').forEach((el) => {
        el.disabled = true;
    });

    window.$.ajax({
        url: form.action,
        type: 'POST',
        dataType: 'json',
        data: {
            rating: Number(formdata.get('chat_rating')),
            comment: formdata.get('client_comment'),
            followup: (followup) ? 1 : 0,
            transcript: (transcript) ? 1 : 0,
            client_contact: this.patronSession.get('client_contact'),
            queue_id: this.model.get('queue_id'),
            iid: this.model.get('iid'),
            page_name: this.model.get('referer_title'),
            page_url: this.model.get('referer'),
            survey_uuid: formdata.get('survey_uuid'),
        },
    })
        .done((data) => {
            this.ratingSaveSuccess(data, formdata);
        })
        .fail(this.ratingSaveError.bind(this));
};
/**
 * Callback: Rating success.
 * @param {Object} data Some kind of data from script
 * @param {FormData} formdata Rating form data
 */
chatView.ratingSaveSuccess = function (data, formdata) {
    if (formdata.get('request_followup')) {
        if (data.ticket.isSuccess) {
            this.model.set('ticketCreated', true);
        }
        this.showChatSysMsg({
            msg: data.ticket.message,
        });
    }
    if (formdata.get('email_transcript')) {
        this.showChatSysMsg({
            msg: data.email.message,
        });
    }
    this.showChatSysMsg({
        msg: this.model.get('translation').ch_comment_success,
    });
    if (this.isBotActive) {
        // Trigger next action (END)
        this.handleBotResponseSubmit();
    }
};
/**
 * Callback: Rating error.
 */
chatView.ratingSaveError = function () {
    this.showChatSysMsg({
        msg: this.model.get('translation').ch_comment_error,
    });
    if (this.isBotActive) {
        // Trigger next action (END)
        this.handleBotResponseSubmit();
    }
};
/**
 * Show a validation error.
 * @param {HTMLElement} field field that error is on.
 * @param {String} msg Message to show.
 */
chatView.showValidationError = function (field, msg) {
    if (msg === '') {
        return;
    }
    const label = field.previousElementSibling;
    if (label.tagName !== 'LABEL') {
        return;
    }
    label.classList.remove('sr-only');
    label.insertAdjacentHTML('beforeend', `<span class="text-danger s-lch-form-error">${msg}</span>`);
};
/**
 * Show the follow_up message if appropriate to settings.
 */
chatView.showFollowUpMsg = function () {
    if (this.model.get('ticketCreated')) {
        return;
    }
    if (!this.model.get('followup_enabled')) {
        return;
    }
    this.enableForm();
    this.showChatSysMsg({
        msg: this.model.get('followup_msg'),
        id: 's-lch-msg-followup',
    });
};
/**
 * Remove the follow_up message if appropriate to settings.
 */
chatView.removeFollowUpMsg = function () {
    this.log.querySelector('#s-lch-msg-followup')?.remove();
};
/**
 * Toggle the sound button icon, text, etc.
 */
chatView.toggleSoundButton = function () {
    const newVal = !this.model.get('enable_sound');
    this.model.set('enable_sound', newVal);

    const soundBtn = this.el.querySelector('.s-lch-btn-sound');
    const icon = soundBtn?.querySelector('i');
    if (!soundBtn || !icon) {
        return;
    }
    let label;

    if (!newVal) {
        label = this.model.get('translation').ch_sound_off;
        icon.classList.remove('fa-volume-up');
        icon.classList.add('fa-volume-off');
    } else {
        label = this.model.get('translation').ch_sound_on;
        icon.classList.remove('fa-volume-off');
        icon.classList.add('fa-volume-up');
    }
    soundBtn.setAttribute('aria-label', label);

    const iconLabel = soundBtn.querySelector('.icon-label');
    if (iconLabel) {
        iconLabel.innerHTML = label;
    }

    const tt = Tooltip.getInstance(soundBtn);
    if (tt) {
        tt.dispose();
        new Tooltip(
            soundBtn,
            {
                title: label,
                html: true,
                placement: soundBtn.dataset?.tooltip ?? 'bottom',
            },
        );
    }
};
/**
 * Set the operator typing status.
 * @param {Object} data Cascade data object.
 * @param {String} data.sessionId session id of typer
 * @param {Number} data.opId Operator id (0 for patron).
 * @param {String} data.room target room
 * @param {Boolean} data.isTyping Typing or not
 * @param {String} data.role op or not
 */
chatView.setOperatorTypingStatus = function (data) {
    if (data.role === 'user') {
        return;
    }
    const opName = this.getOpName(data.opId);
    if (opName === '') {
        return;
    }
    const index = this.currentTypers.indexOf(opName);
    if (data.isTyping) {
        if (index === -1) {
            this.currentTypers.push(opName);
        }
    } else if (index !== -1) {
        this.currentTypers.splice(index, 1);
    }

    if (this.currentTypers.length === 0) {
        this.clearOperatorTyping();
        return;
    }
    this.$chatTyping.html(`${this.currentTypers.join(', ')} ${this.model.get('translation').ch_op_typing}`);
};
/**
 * Clear the isTyping area.
 */
chatView.clearOperatorTyping = function () {
    this.$chatTyping.html('');
};
/**
 * Show the peel out button.
 */
chatView.showPeelButton = function () {
    if (this.inIframe()) {
        this.$el.find('.s-lch-btn-peel').removeClass('hidden');
    }
};
/**
 * Hide the peel out button.
 */
chatView.hidePeelButton = function () {
    this.$el.find('.s-lch-btn-peel').addClass('hidden');
};
/**
 * Close/minimize the widget for float widgets
 */
chatView.closeWidget = function () {
    this.model.emitter.trigger('closeWidget');
};
/**
 * Reload/close chat from feedback/comment form
 */
chatView.reloadCloseChat = function () {
    if ((this.model.isFloat() || this.model.isTab())
        && this.inIframe()) {
        this.model.emitter.trigger('closeWidget');
        return;
    }
    this.model.emitter.trigger('reloadWidget');
};
/**
 * Open the page for emailing a transcript...
 */
chatView.sendTranscript = function () {
    // make a form that we can submit to a new window
    const $form = window.$('<form></form>').attr({
        target: '_blank',
        action: `/chat/session/${this.patronSession.getChannel()}/transcript`,
        method: 'post',
    }).addClass('hidden');

    const inputs = [];

    inputs.push(window.$('<input />').attr({
        type: 'hidden',
        name: 'iid',
    }).val(this.model.get('iid')));

    inputs.push(window.$('<input />').attr({
        type: 'hidden',
        name: 'page_url',
    }).val(this.model.get('referer')));

    inputs.push(window.$('<input />').attr({
        type: 'hidden',
        name: 'page_name',
    }).val(this.model.get('referer_title')));

    $form.append(inputs);
    window.$('body').append($form);
    $form.trigger('submit');
    $form.remove();
};
/**
 * Check the available data about ops for a matching profile image url
 * @param {Number} opId Operator opId
 * @return {Object}
 */
chatView.getProfileImage = function (opId) {
    const profile_image = {
        path: this.model.get('defaultProfile'),
        color: '',
    };
    if (this.operatorsList.length === 0) {
        return profile_image;
    }
    const op = this.operatorsList.find((item) => {
        return Number(item.get('opId')) === Number(opId);
    });
    if (typeof op !== 'undefined') {
        return op.get('profile_image');
    }
    return profile_image;
};
/**
 * Get name for op.
 * @param {Number} opId Op's user id.
 * @returns {String}
 */
chatView.getOpName = function (opId) {
    const op = this.operatorsList.find((item) => {
        return item.get('opId') === opId;
    });
    if (typeof op !== 'undefined') {
        return op.get('name');
    }
    return '';
};
/**
 * Insert messages gathered via ajax request for the "message history" on peelout.
 * @param {Array} messages Message to show
 * @param {String} which Showing all messages or just op messages.
 */
chatView.insertMessageHistory = function (messages, which) {
    if (which === 'all') {
        this.log.setAttribute('aria-live', 'off');
    }
    if (Array.isArray(messages) && messages.length > 0) {
        const op_msgs = window.$('.s-lch-msg-op');
        // add in any messages we don't already have
        messages.forEach(function (msg) {
            if (msg.role === 'note' || msg.system === 'system') {
                return;
            }
            const $match = op_msgs.filter((i, el) => {
                const txt = el.querySelector('.s-lch-msg-txt');
                if (!txt) {
                    return false;
                }
                return txt.innerHTML === msg.msg;
            });
            if ($match.length === 0) {
                if (msg.opId) {
                    msg.profile_image = this.getProfileImage(msg.opId);
                }
                this.showChatMsg(msg);
            }
        }, this);
    }
    if (which === 'all') {
        this.log.setAttribute('aria-live', 'polite');
        // add a "reconnected to previous chat" message.
        this.showChatSysMsg({ msg: this.model.get('translation').ch_reconnect_chat, id: '' });
    } else {
        this.scrollLog();
    }
};
/**
 * The missed chat timeout function.
 */
chatView.showMissedChatMsg = function () {
    this.showChatSysMsg({ msg: this.model.getMissedChatMessage(), id: '' });
};
/**
 * Turn on (if appropriate) the missed chat timeout.
 */
chatView.setMissedChatTimeout = function () {
    if (this.patronSession.get('peel') !== '' || Number(this.model.get('missedchat_time')) <= 0) {
        return;
    }
    this.missedChatTime = window.setTimeout(
        this.showMissedChatMsg.bind(this),
        this.model.get('missedchat_time') * 1000,
    );
};
/**
 * Trigger fallback event.
 */
chatView.triggerFallback = function () {
    this.emitter.trigger('triggerFallback');
};
/**
 * Turn on (if appropriate) the timed fallback timeout.
 * @param {Number} seconds How long until the fallback is triggered.
 */
chatView.setTimedFallbackTimeout = function (seconds) {
    if (this.patronSession.get('peel') !== '' || seconds <= 0) {
        return;
    }
    // Only one fallback timer at a time (Cascade can also fall back on its own).
    this.clearFallbackTimeout();
    this.timedFallbackTime = window.setTimeout(
        this.triggerFallback.bind(this),
        seconds * 1000,
    );
};
/**
 * Clear the fallback timer.
 */
chatView.clearFallbackTimeout = function () {
    window.clearTimeout(this.timedFallbackTime);
};
/**
 * Set bs tooltips to show on buttons/links with font awesome icons in them
 */
chatView.accessibleIcons = function () {
    this.el.querySelectorAll('button > .fa').forEach((el) => {
        const parent = el.closest('button');
        const title = parent?.getAttribute('aria-label') || '';
        if (title !== '') {
            new Tooltip(
                parent,
                {
                    title,
                    html: true,
                    placement: parent.dataset?.tooltip ?? 'bottom',
                },
            );
        }
    });
};
/**
 * Are we in an iframe (helpful for popout btn)
 * @return {Boolean}
 */
chatView.inIframe = function () {
    try {
        return window.self !== window.top;
    } catch {
        return true;
    }
};
/**
 * Play the beep sound
 */
chatView.play_sound = function () {
    try {
        if (this.model.get('enable_sound')) {
            if (!this.audio) {
                this.audio = document.getElementById('sound');
            }
            this.audio.play();
        }
    } catch {
        // ignore
    }
};
/**
 * When an operator joins the chat
 * @param {Object} op Operator object
 */
chatView.showOpJoin = function (op) {
    window.clearTimeout(this.missedChatTime);
    this.clearFallbackTimeout();
    this.play_sound();
    this.showChatSysMsg({ msg: `${op.get('name')} ${this.model.get('translation').ch_op_join}`, id: '' });
    this.removeFollowUpMsg();
};
/**
 * When an operator leaves the chat
 * @param {Object} op Operator object
 */
chatView.showOpLeave = function (op) {
    this.showChatSysMsg({ msg: `${op.get('name')} ${this.model.get('translation').ch_op_left}` });
    this.clearOperatorTyping();
    this.play_sound();
};
/**
 * When an operator non-purposefully dis/re/connects
 * @param {Object} op Operator object
 */
chatView.showOpAway = function (op) {
    this.clearOperatorTyping();
    if (op.get('isAway')) {
        // op pending/away
        this.showChatSysMsg({ msg: `${op.get('name')} ${this.model.get('translation').ch_op_away}` });
        return;
    }
    // op reconnected.
    this.showChatSysMsg({ msg: `${op.get('name')} ${this.model.get('translation').ch_op_back}` });
};
/**
 * Show's department name with a message.
 * On first op join only.
 * @param {String} deptName Department claiming chat.
 */
chatView.showDeptGreeting = function (deptName = '') {
    // show dept greeting too...
    if (deptName === '') {
        return;
    }
    let deptMessage = this.model.get('dept_message');
    if (!deptMessage) {
        return;
    }
    deptMessage = deptMessage.replace('%deptName%', deptName.replace(/&amp;/g, '&'));
    this.showChatSysMsg({
        msg: deptMessage,
    });
    this.emitter.trigger('systemMessage', deptMessage);
};
/**
 * Optional greeting message when first op first joins chat.
 * @param {Boolean} isLocalOp Is the op from the same site as the widget.
 */
chatView.showGreeting = function (isLocalOp) {
    const msg = this.model.getGreeting(isLocalOp);
    this.showChatSysMsg({
        msg,
        id: 'msg_greeting',
    });
    this.emitter.trigger('systemMessage', msg);
};
/**
 * Handler: New message from the server/op.
 * @param {ChatMessage} chatMessage ChatMessage object.
 */
chatView.showNewMessage = function (chatMessage) {
    if (chatMessage.opId === 0) {
        // it's from the patron.
        return;
    }
    this.showChatMsg({
        msgId: chatMessage.msgId,
        msg: chatMessage.msg,
        ts: chatMessage.ts,
        name: this.getOpName(chatMessage.opId),
        opId: chatMessage.opId,
        role: chatMessage.role,
        profile_image: this.getProfileImage(chatMessage.opId),
    });
    this.scrollLog();
    this.play_sound();
    this.enableForm();
};
/**
 * When bot is activated...
 */
chatView.activateBot = function () {
    this.isBotActive = true;
    this.$el.find('.s-lch-header-end').addClass('hidden');
};
/**
 * When bot's request to talk is accepted.
 */
chatView.startBot = function () {
    this.$el.find('.s-lch-header-end').removeClass('hidden');
};
/**
 * When bot is stopped (usually because patron is sent to chat)
 */
chatView.stopBot = function () {
    this.isBotActive = false;
    // reset form for chat.
    this.resetFormForChat();
    this.$el.find('.s-lch-header-end').removeClass('hidden');
    // reset max lengthon textarea
    this.$msgInput[0].removeAttribute('maxlength');
};
/**
 * When bot chat is ended or denied (but patron _not_ sent to chat).
 */
chatView.endBot = function () {
    if (!this.isBotActive) {
        return;
    }
    this.isBotActive = false;
    // Reset form
    this.updateBotForm({});
    this.disableForm();
    this.setEndChatButtonToReload();
    this.$el.find('.s-lch-header-end').removeClass('hidden');
};
/**
 * Update form for bot responses.
 * @param {Number} actionId Current action id.
 * @param {Array<ActionResponse>} responses Action responses
 * @param {Number} actionType Action type.
 */
chatView.updateBotForm = function ({
    actionId = 0,
    responses = [],
    actionType = 0,
}) {
    const templateData = {
        actionId,
        responses: [],
    };
    let freeText = false;
    responses.forEach((response) => {
        if (response.freeResponse) {
            freeText = true;
            return;
        }
        templateData.responses.push(response);
    });

    // insert the response buttons (if any)
    const html = this.templateEng.render('form_bot_responses.njk', templateData);
    this.$form[0].querySelector('.bot-responses').outerHTML = html;

    // make sure this is set for bot.
    this.$msgInput[0].setAttribute('maxlength', 200);
    // hiding/showing textarea
    if (freeText) {
        this.showBotTextInput();
    } else {
        this.hideBotTextInput();
    }
    this.showForm();
    this.enableForm();

    // if responses are in select instead of buttons
    const bSelect = this.$form[0].querySelector('select');
    if (bSelect) {
        bSelect.addEventListener('change', (ev) => {
            this.handleBotSelectOpts(ev);
        });
        // select requires the submit button
        this.submitBtn.classList.remove('hidden');
    }

    // focus first focusable in form if there is one, except in new embed widget welcome
    if (this.model.isEmbed() && actionType === flowActionTypes.ACTION_WELCOME) {
        return;
    }
    const focusable = this.$form[0].querySelector('textarea:not(.hidden), button:not(.hidden), select');
    if (focusable) {
        this.scrollLog();
        focusable.focus();
    }
};

chatView.showBotTextInput = function () {
    this.$msgInput[0].classList.remove('hidden');
    this.submitBtn.classList.remove('hidden');
};

chatView.hideBotTextInput = function () {
    this.$msgInput[0].classList.add('hidden');
    this.$msgInput[0].value = '';
    this.submitBtn.classList.add('hidden');
};

/**
 * When response buttons are clicked
 * @param {HTMLElement} target Chosen option (button)
 */
chatView.handleBotResponseOpts = function ({ target = null }) {
    if (!target) {
        return;
    }
    const responseId = Number(target.dataset.responseid || 0);
    const responseInput = this.$form[0].querySelector('input[name="responseid"]');
    if (responseInput) {
        responseInput.value = responseId;
    }
    const isOther = Number(target.dataset.other || 0) === 1;
    if (isOther) {
        // trigger the text input.
        this.showBotTextInput();
        return;
    }
    this.hideBotTextInput();
    this.handleBotResponseSubmit();
};
/**
 * Change handler for select in bot form
 * @param {HTMLSelectElement} target Select in bot form
 */
chatView.handleBotSelectOpts = function ({ target = null }) {
    if (!target) {
        return;
    }
    const option = target.options[target.selectedIndex] || null;
    if (!option) {
        return;
    }
    const isOther = Number(option.dataset.other || 0) === 1;
    if (isOther) {
        // trigger the text input.
        this.showBotTextInput();
        return;
    }
    // submit option
    this.hideBotTextInput();
    // need submit button for select
    this.submitBtn.classList.remove('hidden');
};

/**
 * Renew click from Patron Record bot action
 * @param {ClickEvent} ev
 */
chatView._handleBotRenew = function (ev) {
    ev.preventDefault();
    const link = ev.target;
    const itemId = link.dataset.itemid || '';
    if (!itemId) {
        return;
    }
    const actEl = link.closest('[data-actionid]');
    const actionId = Number(actEl?.dataset.actionid || 0);
    if (actionId > 0) {
        this.emitter.trigger('bot:renewitem', { itemId, actionId });
    }
};

/**
 * Handler: New message from the bot.
 * @param {BotMessage} botMessage BotMessage object.
 * @param {Boolean} endChat End the chat after showing the message (good for error messages)
 */
chatView.showBotMessage = function ({ botMessage = null, endChat = false }) {
    const data = {
        actionId: botMessage.actionId,
        msg: botMessage.msg,
        ts: new Date(botMessage.ts),
        name: botMessage.name,
        role: botMessage.role,
        profile_image: {
            path: botMessage.profile_url,
            color: botMessage.profile_color,
        },
        ts_html: '',
        ts_time: '',
        show_meta: botMessage.msgId === 1,
    };

    // format time for time element pubdate attr and for human readability
    if (data.ts !== '' && data.ts !== 'Invalid Date') {
        data.ts_html = this.htmlTime(data.ts);
        data.ts_time = data.ts.toLocaleTimeString();
    }

    const html = this.templateEng.render('message_bot.njk', data);
    this.log.insertAdjacentHTML('beforeend', html);
    if (botMessage.actionType !== flowActionTypes.ACTION_WELCOME) {
        this.scrollLog();
    }
    this.play_sound();

    if (!botMessage.isError) {
        this.updateBotForm({
            actionId: botMessage.actionId,
            responses: botMessage.responses,
            actionType: botMessage.actionType,
        });
    }
    // do this after form update cause it will hide/disable the form
    if (botMessage.actionType === flowActionTypes.ACTION_RATING) {
        this.showRatingForm(true, botMessage);
    }

    if (endChat) {
        this.endBot();
    }
};
/**
 * Handler for paging responses (like Booking dates)
 * Just updates the bot form.
 */
chatView.showPagedOptions = function ({ actionId, actionType = 0, responses = [] }) {
    this.updateBotForm({
        actionId,
        responses,
        actionType,
    });
};
/**
 * Handle skipping bot and going right to chat
 * Should trigger the "skip login" mode of the widget.
 */
chatView.skipBot = function () {
    this.isBotActive = false;
    // just rerender without bot
    // if we add "skip to chat" as an option for non-welcome message
    // then we'd probably want to keep the bot messages around...

    // reset flow_id and set skip_login so the nologin_message shows
    this.model.set('flow_id', '');
    this.model.set('skip_login', true);
    this.render();
};
/**
 * Remove a message an op deleted.
 * @param {Number} msgId unique message id
 */
chatView.removeMessage = function (msgId) {
    if (msgId <= 0) {
        return;
    }
    const msg = this.log.querySelector(`li[data-msgid="${msgId}"]`);
    if (msg) {
        // If this message showed profile pic then we reveal it on the subsequent message
        const showingMeta = !msg.classList.contains('chatMsgNoMeta');
        if (showingMeta) {
            const nextMsg = msg.nextElementSibling;
            if (nextMsg) {
                nextMsg.classList.remove('chatMsgNoMeta');
            }
        }
        msg.remove();
    }
};
/**
 * When the patron has posted a message.
 * @param {String} message The text of the message.
 */
chatView.showPatronMessage = function (message) {
    this.clearTextarea(true);
    this.showChatMsg({
        msg: message,
        name: 'You',
        role: 'user',
    });
    this.scrollLog();
};
/**
 * Convert the "end chat" button to a "reload widget" button.
 */
chatView.setEndChatButtonToReload = function () {
    const newText = this.model.get('restart_chat');
    const endBtn = this.el.querySelector('.s-lch-btn-end');
    endBtn.classList.remove('hidden');
    endBtn.setAttribute('aria-label', newText);
    const icon = endBtn.querySelector('i');
    if (icon) {
        icon.classList.remove('fa-times');
        icon.classList.add('fa-repeat');
    }
    const tt = Tooltip.getInstance(endBtn);
    if (tt) {
        tt.dispose();
        new Tooltip(
            endBtn,
            {
                title: newText,
                html: true,
                placement: endBtn.dataset?.tooltip ?? 'bottom',
            },
        );
    }
};
/**
 * View actions for end of chat.
 * @param {Boolean} purposeful Did the op/patron purposefully end the chat.
 */
chatView.endChatDisplay = function (purposeful) {
    window.clearTimeout(this.missedChatTime);
    this.clearFallbackTimeout();

    this.setEndChatButtonToReload();

    if (!purposeful || !this.model.get('answered')) {
        // Let them know they can ask for a follow-up by submitting message.
        this.showFollowUpMsg();
        return;
    }

    this.disableForm();

    // are we asking for a rating/feedback?
    if (this.model.get('star_ratings') === true) {
        this.showRatingForm();
        return;
    }

    this.showChatSysMsg({
        msg: this.model.get('byeMsg'),
    });
};
/**
 * Display when patron successfully subscribes to chat
 * @param {Number} onlineStatus Online status
 */
chatView.chatSubscribeDisplay = function (onlineStatus) {
    this.disableForm();
    window.$('#nologin_message').remove();
    // Set from connecting.... to connected.
    this.setConnectState();

    if (this.patronSession.get('client_question') !== '') {
        this.showChatMsg({
            msg: this.patronSession.get('client_question'),
            name: 'You',
            role: 'user',
        });
    }
    if (onlineStatus === this.model.get('status_away')) {
        this.showChatSysMsg({ msg: this.model.getAwayMessage(), id: 'msg_away' });
    } else {
        this.showChatSysMsg({ msg: this.model.get('wait'), id: '' });
    }
    if (this.model.get('contact_request') && this.model.get('client_contact') === '') {
        this.showContactRequestForm();
    }
    this.setMissedChatTimeout();
    this.emitter.trigger('setFirstFallback');
};
/**
 * Update display when chatting starts
 */
chatView.enableChatting = function () {
    if (this.model.get('chatting')) {
        this.enableForm();
        this.$msgInput.focus();
        this.$el.find('.s-lch-btn-trans, .s-lch-btn-file').removeClass('hidden');
        this.showPeelButton();
    }
};
/**
 * What to do when widget peels out.
 */
chatView.peeledOutDisplay = function () {
    if (this.model.get('peelingParent')) {
        this.disableForm();
    }
};
/**
 * Set the UI to connect/disconnect status
 */
chatView.setConnectState = function () {
    const connected = this.model.get('connected');

    let className;
    let text;

    if (!this.model.hasSubscribed) {
        className = 'connected';
        text = this.model.get('translation').ch_status_connecting;
    } else if (connected) {
        className = 'connected';
        text = this.model.get('translation').ch_status_connected;
    } else {
        className = 'disconnected';
        text = this.model.get('translation').ch_status_disconnected;
        this.$el.find('.s-lch-btn-peel, .s-lch-btn-file').addClass('hidden');
    }

    window.$('#s-lch-status')
        .removeClass('connected disconnected')
        .addClass(className)
        .html(`<span class="sr-only">${this.model.get('translation').ch_status_label}: </span>${text}`);
};
/**
 * When a chat is reconnecting after non-purposeful disconnect
 */
chatView.showReconnecting = function () {
    this.showChatSysMsg({
        msg: this.model.get('translation').ch_connect_lost,
        id: 'msg_reconnecting',
    });
};
/**
 * When a chat is reconnected. This is followed by a connect.
 */
chatView.showReconnected = function () {
    this.showChatSysMsg({
        msg: this.model.get('translation').ch_connect_regained,
        id: 'msg_reconnecting',
    });
    this.enableForm();
    this.$el.find('.s-lch-btn-file').removeClass('hidden');
    this.showPeelButton();
};
/**
 * When cascade/chat errors out.
 * @param {String} error Optional error text
 */
chatView.showChatError = function (error = '') {
    this.showChatSysMsg({
        msg: (!error) ? this.model.getErrorMessage() : error,
        id: 'msg_error',
    });
};
/**
 * Error when chat was banned/blocked.
 * Replaces whole view so all actions are stopped.
 * @param {String} error Error text.
 */
chatView.showBlockedError = function (error = '') {
    const msg = (!error) ? this.model.getErrorMessage() : error;
    this.$el.html(`<p class="alert alert-danger">${msg}</p>`);
};
/**
 * When you get connected but the widget is away.
 * For the skip login form mode of widget.
 */
chatView.showChatAwayStatus = function () {
    this.showChatSysMsg({
        msg: this.model.getAwayMessage(),
        id: 'msg_away',
    });
    this.showFollowUpMsg();
};
/**
 * Disable the chat form
 */
chatView.disableForm = function () {
    this.$msgInput.prop('disabled', true);
    this.submitBtn.disabled = true;
};
/**
 * Hide the chat form
 */
chatView.hideForm = function () {
    this.$form.addClass('hidden');
};
/**
 * Show the chat form
 */
chatView.showForm = function () {
    this.$form[0].classList.remove('hidden');
};
/**
 * Enable the chat form
 */
chatView.enableForm = function () {
    this.$msgInput.prop('disabled', false);
    this.submitBtn.disabled = false;
};
/**
 * Reset the form display for normal chat.
 * Clears out bot response area, displays text input and button.
 */
chatView.resetFormForChat = function () {
    const botResponses = this.$form[0].querySelector('.bot-responses');
    if (botResponses) {
        botResponses.innerHTML = '';
        botResponses.classList.add('hidden');
    }

    // Showing textarea and button
    this.$form[0].querySelector('#s-lch-post-input').classList.remove('hidden');
    this.submitBtn.classList.remove('hidden');
};
/**
 * Show this view and set focus as appopriate
 */
chatView.showView = function () {
    this.$el.removeClass('hidden');
    if (this.model.get('auth_id') > 0 && !this.model.get('isAuthValidated')) {
        // When LibAuth overlay is shown we want to make this view hidden for screenreaders
        // And disable any form elements from keyboard interaction.
        // Disable inputs to stop focus
        this.$el[0].querySelectorAll('input,select,button,textarea').forEach((el) => {
            el.disabled = true;
        });
        // make tabbable elements and links untabbable.
        this.$el[0].querySelectorAll('[tabindex="0"],a').forEach((el) => {
            el.setAttribute('tabindex', '-1');
        });
        // hide form for screenreaders
        this.$el[0].setAttribute('aria-hidden', 'true');
        return;
    }
    // avoid focus for embedded widgets when they first load.
    if (this.model.isEmbed() && !this.model.get('answered') && !this.isBotActive) {
        return;
    }
    if (this.$msgInput.disabled) {
        focusFirst(this.el);
        return;
    }
    this.$msgInput.focus();
};
/**
 * Show the close button or not.
 */
chatView.toggleCloseButton = function () {
    if (!this.model.isFloat() || this.model.peel) {
        this.el.querySelector('.s-lch-btn-close')?.classList.add('hidden');
    }
};
/**
 * Preview of images opens a new page.
 * @param {Event} ev Click event on link
 */
chatView.showFileUploadPreview = function (ev) {
    const link = ev.currentTarget;
    const url = new URL(link.href);
    const uuid = url.pathname.split('/').pop();
    if (uuid) {
        ev.preventDefault();
        const href = `/chat/session/${this.patronSession.getChannel()}/upload/${uuid}/preview`;
        // open in a new window/tab
        window.open(href, '_blank');
    }
};
/**
 * When patron tries to send message but they are disconnected and the followup msg has not been shown yet.
 */
chatView.showMessageNotSendable = function () {
    this.showChatSysMsg({
        msg: this.model.get('translation').ch_message_unsendable,
    });
};
/**
 * Handler: open links in new tabs if not otherwise handled.
 * This will cover ALL children of this view.
 * @param {Object} ev Click event.
 */
chatView.openNewTab = function (ev) {
    // if its already handled elsewhere.
    if (ev.defaultPrevented) {
        return;
    }
    const a = ev.currentTarget;
    const href = a.getAttribute('href');
    // Ignore anchor links
    if (href.indexOf('#') === 0) {
        return;
    }

    // intercept bot action clicks to record stats
    if (this.isBotActive && a.classList.contains('action_click')) {
        const actEl = a.closest('[data-actionid]');
        if (actEl) {
            const actionId = Number(actEl.dataset.actionid || 0);
            if (actionId > 0) {
                this.emitter.trigger('bot:click', { actionId, url: a.href });
            }
        }
    }

    const target = a.getAttribute('target');
    // Ignore blank targets (already doing what we want)
    if (target === '_blank') {
        return;
    }
    // stop default link action.
    ev.preventDefault();
    // stop here on empty hrefs or just #
    if (!href || href === '#') {
        ev.preventDefault();
        return;
    }
    // open in a new window/tab
    window.open(href, '_blank');
};
/**
 * Render the view
 * @return {autoView}
 */
chatView.render = function () {
    // clear out existing content, events, etc.
    this.$el.empty();

    // render the template and put it in the dom.
    const templateData = this.model.toJSON();
    templateData.inIframe = this.inIframe();
    this.$el.html(this.templateEng.render('pane_chatting.njk', templateData));

    this.$form = this.$el.find('#s-lch-post-form');
    this.$msgInput = this.$form.find('textarea');
    this.submitBtn = this.$form[0].querySelector('button[type=submit]');
    this.log = this.el.querySelector('#s-lch-msg-log');
    this.imgContainer = this.el.querySelector('#s-lch-preview-container');
    this.$chatTyping = this.$el.find('#istyping');
    this.contactForm = null;

    this.toggleCloseButton();
    // tooltips for icons
    this.accessibleIcons();

    if (this.model.isEmbed()) {
        this.el.classList.add('breakfocus');
    }

    return this;
};

export default chatView;
